import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { ChevronDown } from '@lifeomic/chromicons';
import clsx from 'clsx';
import SurveyItem from '../../util/SurveyItem';
import { SurveyItemComponent } from '../SurveyItem';
import ConfirmStrip from './ConfirmStrip';
import { Text } from '@lifeomic/chroma-react/components/Text';
import getSurveyItemTestId from '../../util/getSurveyItemTestId';
import { getTestProps } from '../../../../util/testProps';
import withCheckForEnablement, {
  WithCheckForEnablementInjectedProps,
} from '../hoc/withCheckForEnablement';
import withIsMobile, {
  WithIsMobileInjectedProps,
} from '../../../../hoc/withIsMobile';
import {
  withSurveyResponseContext,
  WithSurveyResponseContextInjectedProps,
} from '../Contexts';

export const testIds = {
  sectionToggle: (linkId: string) =>
    getSurveyItemTestId(linkId, 'section-toggle'),
  sectionContent: (linkId: string) =>
    getSurveyItemTestId(linkId, 'section-content'),
  groupLine: (linkId: string, lineIndex: number) =>
    getSurveyItemTestId(linkId, `line-${lineIndex}`),
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: theme.spacing(6),
    },
    subItemRoot: {
      // No explicit width: `width: 100%` plus horizontal margins makes the
      // outer box wider than the section, pushing table right edges (e.g.
      // matrix YES/NO columns) past the visible area. Block auto-width
      // subtracts the margins so content always fits the section.
      margin: theme.spacing(2),
    },
    collapsibleRoot: {
      width: '100%',
      marginTop: theme.spacing(4),
    },
    sectionHeader: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1.5, 2),
      background: theme.palette.grey[100],
      border: 'none',
      borderBottom: `2px solid ${theme.palette.grey[300]}`,
      borderRadius: theme.pxToRem(4),
      cursor: 'pointer',
      textAlign: 'left',
    },
    sectionChevron: {
      color: theme.palette.text.secondary,
      transition: 'transform 150ms ease',
      flexShrink: 0,
    },
    sectionChevronCollapsed: {
      transform: 'rotate(-90deg)',
    },
    // Collapsed sections stay mounted (display:none) so their Formik fields
    // remain registered and validation still runs on submit.
    sectionItemsCollapsed: {
      display: 'none',
    },
    // Page-level confirm strips are hoisted into SurveyLayout sticky chrome;
    // this wrapper is the strip + hidden child fields for autopopulate.
    confirmRoot: {
      width: '100%',
    },
    // Unlabeled groups used as table-row-detail wrappers (e.g. pairing
    // grip kg + rank on one line) should not add the section's 48px top
    // margin or inner inset; the detail well already provides that.
    nestedDetailRoot: {
      width: '100%',
    },
    // Mirrors PaginatedSurveyForm's formLine so same-line items pair up
    // inside groups exactly like they do as direct page children.
    formLine: {
      width: '100%',
      display: 'flex',
      // bottom-align so inputs stay on one line when a label wraps
      alignItems: 'flex-end',
      gap: theme.spacing(2),
    },
    // Mobile stacking is CSS-only: the line wrappers always render so the
    // element tree keeps the same shape across the breakpoint. Otherwise
    // resizing across it would remount every child and wipe local UI state
    // (e.g. this component's isCollapsed).
    formLineStacked: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  });

export interface GroupOwnProps {
  surveyItem: SurveyItem;
}

export type GroupProps = WithStyles<typeof styles> &
  WithCheckForEnablementInjectedProps &
  WithIsMobileInjectedProps &
  // surveyResponse is unused in render; the injection exists so PureComponent
  // re-renders when repeating-group instances change without remounting.
  WithSurveyResponseContextInjectedProps &
  GroupOwnProps;

interface GroupState {
  isCollapsed: boolean;
}

class Group extends PureComponent<GroupProps, GroupState> {
  state: GroupState = {
    isCollapsed: this.props.surveyItem.startsCollapsed,
  };

  handleToggle = () => {
    this.setState(prevState => ({ isCollapsed: !prevState.isCollapsed }));
  };

  getHeaderText = () => {
    const {
      surveyItem: { prefix, text },
    } = this.props;

    if (prefix) {
      return `(${prefix}) ${text}`;
    }
    return text || '';
  };

  renderSubItems = () => {
    const { surveyItem, classes, isMobile } = this.props;

    // Honor the same-line extension inside groups: without this, nested
    // section groups stack every item (e.g. Height feet/inches) one per row.
    // The wrappers render on mobile too (stacked via CSS) so that crossing
    // the breakpoint never changes the tree shape and remounts children.
    return surveyItem.subItemsGroupedByDisplayLines.map(
      (lineSurveyItems, index) => (
        <div
          className={clsx(
            classes.formLine,
            isMobile && classes.formLineStacked,
          )}
          key={lineSurveyItems[0]?.link ?? index}
          {...getTestProps(testIds.groupLine(surveyItem.link, index))}
        >
          {lineSurveyItems.filter(Boolean).map(lineSurveyItem => (
            <SurveyItemComponent
              key={lineSurveyItem.link}
              surveyItem={lineSurveyItem}
            />
          ))}
        </div>
      ),
    );
  };

  renderCollapsibleSection = () => {
    const { surveyItem, classes } = this.props;
    const { isCollapsed } = this.state;
    const contentId = `${surveyItem.link}-section-content`;

    return (
      <div className={classes.collapsibleRoot}>
        {/* type="button" is required: the survey renders inside a <form>,
            and a default (submit) button would Next/Complete the survey */}
        <button
          type="button"
          className={classes.sectionHeader}
          onClick={this.handleToggle}
          aria-expanded={!isCollapsed}
          aria-controls={contentId}
          {...getTestProps(testIds.sectionToggle(surveyItem.link))}
        >
          <Text size="body" weight="bold">
            {this.getHeaderText()}
          </Text>
          <ChevronDown
            aria-hidden
            className={clsx(
              classes.sectionChevron,
              isCollapsed && classes.sectionChevronCollapsed,
            )}
          />
        </button>
        <div
          id={contentId}
          className={clsx(
            classes.subItemRoot,
            isCollapsed && classes.sectionItemsCollapsed,
          )}
          {...getTestProps(testIds.sectionContent(surveyItem.link))}
        >
          {this.renderSubItems()}
        </div>
      </div>
    );
  };

  renderConfirmStrip = () => {
    const { surveyItem, classes } = this.props;

    return (
      <div className={classes.confirmRoot}>
        <ConfirmStrip surveyItem={surveyItem} />
        {/* Hidden so autopopulate still writes Formik/response values for
            the note template, without showing intake fields. */}
        <div className={classes.sectionItemsCollapsed} aria-hidden>
          {this.renderSubItems()}
        </div>
      </div>
    );
  };

  render() {
    const { surveyItem, classes, isEnabled } = this.props;

    if (!isEnabled) return null;

    if (surveyItem.isConfirmStrip) {
      return this.renderConfirmStrip();
    }

    if (surveyItem.isCollapsibleSection) {
      return this.renderCollapsibleSection();
    }

    const headerText = this.getHeaderText();

    if (surveyItem.isTableRowDetail && !headerText) {
      return (
        <div className={classes.nestedDetailRoot}>{this.renderSubItems()}</div>
      );
    }

    return (
      <div className={classes.root}>
        {headerText ? <Text size="headline">{headerText}</Text> : null}
        <div className={classes.subItemRoot}>{this.renderSubItems()}</div>
      </div>
    );
  }
}

export default withIsMobile(
  withStyles(styles)(withCheckForEnablement(withSurveyResponseContext(Group))),
);
